import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import axios from 'axios';
import { API_BASE_URL } from '../constants';

interface BackendStatusContextType {
  isBackendAvailable: boolean;
  pauseHealthCheck: () => void;
  resumeHealthCheck: () => void;
}

const BackendStatusContext = createContext<BackendStatusContextType | undefined>(undefined);

export const BackendStatusProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [isBackendAvailable, setIsBackendAvailable] = useState(true);
  const [isHealthCheckPaused, setIsHealthCheckPaused] = useState(false);
  
  // Use refs to store timeout and abort controller so they can be accessed from pause/resume
  const timeoutIdRef = React.useRef<NodeJS.Timeout | null>(null);
  const abortControllerRef = React.useRef<AbortController | null>(null);
  const isBackendAvailableRef = React.useRef<boolean>(true); // Track current state for scheduling

  // Expose pause/resume functions
  const pauseHealthCheck = React.useCallback(() => {
    setIsHealthCheckPaused(true);
    // Cancel any pending health check
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
    // Clear any scheduled checks
    if (timeoutIdRef.current) {
      clearTimeout(timeoutIdRef.current);
      timeoutIdRef.current = null;
    }
  }, []);

  const resumeHealthCheck = React.useCallback(() => {
    setIsHealthCheckPaused(false);
    // Health checks will resume automatically when isHealthCheckPaused changes
  }, []);

  useEffect(() => {
    // Set up axios interceptor to detect backend connection errors
    // We'll use a separate axios instance for health checks to avoid intercepting all requests
    const healthCheckAxios = axios.create();
    
    // Add response interceptor to health check axios instance to suppress console errors
    // This interceptor catches errors before axios logs them to console
    const healthCheckInterceptor = healthCheckAxios.interceptors.response.use(
      (response) => response,
      (error) => {
        // Suppress console errors for health check requests
        // Only treat as error if it's a real connection issue (not 401/404)
        if (
          error.code === 'ECONNREFUSED' ||
          error.code === 'ERR_NETWORK' ||
          error.code === 'ECONNABORTED' ||
          (error.response && error.response.status >= 500)
        ) {
          // These are real errors, let them through
          return Promise.reject(error);
        }
        // For 401, 404, etc., silently resolve as these mean backend is up
        // This prevents axios from logging to console
        return Promise.resolve({ 
          data: {}, 
          status: error.response?.status || 200,
          statusText: error.response?.statusText || 'OK',
          headers: error.response?.headers || {},
          config: error.config
        });
      }
    );
    
    const responseInterceptor = axios.interceptors.response.use(
      (response) => {
        // If we get a successful response, backend is available
        setIsBackendAvailable(true);
        return response;
      },
      (error) => {
        // Suppress console errors for expected status codes (401, 404, etc.)
        void (error.response && 
          (error.response.status === 401 || error.response.status === 404));
        
        // Check if it's a network error or connection refused
        // Note: 500 errors mean backend IS reachable (it responded), just has a server error
        // Only treat network errors as "unreachable"
        if (
          error.code === 'ECONNREFUSED' ||
          error.code === 'ERR_NETWORK' ||
          error.code === 'ERR_INTERNET_DISCONNECTED' ||
          (error.request && !error.response)
        ) {
          setIsBackendAvailable(false);
        } else {
          // For other errors (including 500), backend is still available (it responded)
          setIsBackendAvailable(true);
        }
        return Promise.reject(error);
      }
    );

    // Periodically check backend availability
    // Use AbortController to allow cancellation and implement retry logic
    let isChecking = false; // Prevent concurrent checks
    let consecutiveFailures = 0; // Track consecutive failures
    const MAX_CONSECUTIVE_FAILURES = 3; // Require 3 failures before marking as unavailable
    
    const checkBackendHealth = async () => {
      // Skip if health check is paused (e.g., during restore operations)
      if (isHealthCheckPaused) {
        return;
      }
      
      // Skip if already checking to prevent concurrent requests
      if (isChecking) {
        return;
      }
      
      // Cancel previous request if still pending
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      
      abortControllerRef.current = new AbortController();
      const currentController = abortControllerRef.current;
      isChecking = true;
      
      try {
        // Use /api/health endpoint - it's simpler and faster (no DB queries, no auth)
        // Increased timeout to 5 seconds to account for NAS network latency
        await healthCheckAxios.get(`${API_BASE_URL}/api/health`, {
          timeout: 5000, // Increased from 2000ms to 5000ms for NAS network latency
          signal: currentController.signal,
          validateStatus: (status) => status < 500 // Accept any status < 500 as "backend is up"
        });
        
        // Success - reset failure counter
        consecutiveFailures = 0;
        
        // If we get here, backend is responding
        // Only update state if it changed to avoid unnecessary re-renders
        setIsBackendAvailable(prev => {
          if (prev === false) {
            isBackendAvailableRef.current = true;
            return true;
          }
          return prev;
        });
      } catch (error: any) {
        // Ignore aborted requests
        if (error.name === 'AbortError' || error.code === 'ERR_CANCELED') {
          isChecking = false;
          return;
        }
        
        // Increment failure counter
        consecutiveFailures++;
        
        // Only mark as unavailable after multiple consecutive failures
        // This prevents false positives from temporary network issues
        const isUnavailable = consecutiveFailures >= MAX_CONSECUTIVE_FAILURES && (
          error.code === 'ECONNREFUSED' ||
          error.code === 'ERR_NETWORK' ||
          error.code === 'ECONNABORTED' ||
          (error.request && !error.response)
        );
        
        // Note: Timeout errors (ECONNABORTED) might be due to slow NAS response
        // Don't immediately mark as unavailable - wait for multiple failures
        if (isUnavailable) {
          isBackendAvailableRef.current = false;
          setIsBackendAvailable(false);
        } else if (consecutiveFailures < MAX_CONSECUTIVE_FAILURES) {
          // Still have retries left, keep backend as available
          isBackendAvailableRef.current = true;
          setIsBackendAvailable(true);
        }
      } finally {
        isChecking = false;
      }
    };

    // Check immediately and then every 20 seconds (increased to reduce load and false positives)
    // Use setTimeout recursion instead of setInterval for better control
    // If backend is unavailable, check more frequently (every 10s) to detect recovery faster
    const scheduleNextCheck = () => {
      const checkInterval = isBackendAvailableRef.current ? 20000 : 10000; // 20s if available, 10s if unavailable
      timeoutIdRef.current = setTimeout(() => {
        checkBackendHealth().finally(() => {
          if (!isHealthCheckPaused) {
            scheduleNextCheck(); // Schedule next check after current one completes
          }
        });
      }, checkInterval);
    };
    
    // Only start checks if not paused
    if (!isHealthCheckPaused) {
      checkBackendHealth().finally(() => {
        if (!isHealthCheckPaused) {
          scheduleNextCheck();
        }
      });
    }

    return () => {
      axios.interceptors.response.eject(responseInterceptor);
      healthCheckAxios.interceptors.response.eject(healthCheckInterceptor);
      // Clear timeout instead of interval
      if (timeoutIdRef.current) {
        clearTimeout(timeoutIdRef.current);
        timeoutIdRef.current = null;
      }
      // Cancel any pending health check request
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
        abortControllerRef.current = null;
      }
    };
  }, [isHealthCheckPaused]);

  return (
    <BackendStatusContext.Provider value={{ 
      isBackendAvailable, 
      pauseHealthCheck, 
      resumeHealthCheck 
    }}>
      {children}
    </BackendStatusContext.Provider>
  );
};

export const useBackendStatus = (): BackendStatusContextType => {
  const context = useContext(BackendStatusContext);
  if (context === undefined) {
    throw new Error('useBackendStatus must be used within a BackendStatusProvider');
  }
  return context;
};

